/**
 * DOCX export + shared DOM-cleanup for the Plate editor.
 *
 * Two responsibilities:
 *   1. `cleanDomForExport` — strips editor-only UI (resize handles, delete
 *      buttons, slate-* attributes), normalises tables/images/headings to
 *      a portable shape, and prepares a snapshot for either the clipboard
 *      copy path or the DOCX export. Called from both `handleCopy` inside
 *      PlateEditor and from `exportEditorToDocxFile` below.
 *   2. `exportEditorToDocxFile` — converts the Plate value tree to real
 *      OOXML (.docx) using the `docx` library and triggers a download.
 *
 * Pure DOM helpers — no React, no Plate API. Safe to call outside the
 * Plate provider context.
 */

import {
    AlignmentType,
    BorderStyle,
    Document,
    ExternalHyperlink,
    HeadingLevel,
    ImageRun,
    LevelFormat,
    Packer,
    Paragraph,
    ShadingType,
    Table,
    TableCell,
    TableRow,
    TextRun,
    WidthType,
} from 'docx';
import { fetchImageUrlsBulk, isDocId } from '../services/imageUploadService';

// ─────────────────────────────────────────────────────────────────────────────
// cleanDomForExport — kept intact; used by clipboard copy path.
// ─────────────────────────────────────────────────────────────────────────────

// Shared DOM cleanup for clipboard copy + DOCX export.
// Mutates `wrapper` in place; pass a CLONE if you don't want the live
// editor DOM modified (clipboard copy path does `range.cloneContents()`
// before calling this; DOCX export clones the editor content element).
//
// Word treats unknown CSS classes, CSS variables, SVGs, and form elements
// as foreign markup. If any one of those leaks through, the user sees the
// "Word found unreadable content" prompt and (often) opens a stripped /
// broken document. This pass is aggressive on purpose — we keep semantic
// tags + inline styles Word actually understands.
export function cleanDomForExport(wrapper: HTMLElement) {
    // 1. Headings: convert any `data-slate-type="h*"` styled-div to a real
    //    <h1>/<h2>/<h3> tag BEFORE attribute scrubbing, since the slate-type
    //    attribute is the only signal of intent.
    wrapper.querySelectorAll('[data-slate-type]').forEach((el) => {
        const slateType = el.getAttribute('data-slate-type') || '';
        const tag = el.tagName.toLowerCase();
        if (/^h[1-6]$/.test(slateType) && !/^h[1-6]$/.test(tag)) {
            const heading = document.createElement(slateType);
            heading.innerHTML = el.innerHTML;
            el.replaceWith(heading);
        }
    });

    // 2. Drop editor-only UI: resize handles, delete buttons, upload chips,
    //    contenteditable="false" wrappers (other than legitimate void cells
    //    and inline media). Plate's interactive widgets all carry
    //    contenteditable=false on their root.
    wrapper
        .querySelectorAll(
            '[class*="handle"], [class*="deleteBtn"], [class*="Handle"], [class*="uploadChip"], [contenteditable="false"]:not(td):not(th):not(table):not(img):not(span)',
        )
        .forEach((el) => el.remove());

    // 3. Hard-remove element types Word's HTML parser chokes on.
    wrapper
        .querySelectorAll('script, style, link, meta, button, input, textarea, select, svg, canvas, video, audio, iframe, noscript')
        .forEach((el) => el.remove());

    // 4. Tables: borders + padding for Word/Google Docs.
    wrapper.querySelectorAll('table').forEach((t) => {
        t.setAttribute('style', 'border-collapse: collapse; width: 100%; margin: 4px 0; line-height: 1.15;');
        t.setAttribute('border', '1');
        t.setAttribute('cellpadding', '4');
        t.setAttribute('cellspacing', '0');
    });
    wrapper.querySelectorAll('td, th').forEach((cell) => {
        (cell as HTMLElement).setAttribute('style', 'border: 1px solid #999999; padding: 4px 8px; vertical-align: top; line-height: 1.15;');
    });
    wrapper.querySelectorAll('th').forEach((th) => {
        (th as HTMLElement).setAttribute(
            'style',
            'border: 1px solid #999999; padding: 4px 8px; vertical-align: top; line-height: 1.15; background-color: #f5f5f3; font-weight: 600;',
        );
    });

    // 5. Images. Drop blob:/data: src that won't survive disk transfer (Word
    //    can't resolve them once the file is moved). Keep https:// and data:image/*
    //    base64 (those embed inline). Drop empty src to avoid Word's
    //    "missing image" placeholder.
    wrapper.querySelectorAll('img').forEach((img) => {
        const src = img.getAttribute('src') || '';
        if (!src || src.startsWith('blob:')) {
            img.remove();
            return;
        }
        const w = img.getAttribute('width') || img.style.width;
        if (w) img.setAttribute('width', String(parseInt(w)));
        // Word respects width but treats class/srcset/loading hints as foreign.
        ['class', 'srcset', 'sizes', 'loading', 'decoding', 'data-doc-id', 'data-slate-fragment'].forEach((a) => img.removeAttribute(a));
    });

    // 6. Final attribute scrub on EVERY remaining element. Whitelist of
    //    attributes Word understands; drop everything else (class hashes,
    //    aria-*, role, data-*, contenteditable, draggable, slate refs, etc.).
    //    Style attributes are kept but stripped of `var(--…)` references that
    //    won't resolve in Word.
    const allowedAttrs = new Set([
        // Layout / appearance
        'style',
        'href',
        'src',
        'alt',
        'title',
        'width',
        'height',
        'colspan',
        'rowspan',
        'align',
        'valign',
        // Tables
        'border',
        'cellpadding',
        'cellspacing',
        'bgcolor',
        // Lists
        'start',
        'type',
        // Links
        'target',
        'rel',
    ]);
    const allElements = wrapper.querySelectorAll('*');
    allElements.forEach((el) => {
        // Snapshot attribute names — removing while iterating mutates the live list.
        const names = Array.from(el.attributes).map((a) => a.name);
        for (const name of names) {
            if (!allowedAttrs.has(name)) {
                el.removeAttribute(name);
                continue;
            }
            // Strip CSS variables from style — Word renders `var(--brand)` as
            // a literal string and the surrounding rule may be dropped.
            if (name === 'style') {
                const raw = el.getAttribute('style') || '';
                const cleaned = raw
                    // Drop var(...) usages entirely (plus the rule containing them).
                    .replace(/[^;]*var\([^)]*\)[^;]*;?/g, '')
                    // Drop unsupported modern CSS Word can't render: container queries,
                    // ::has, :where, custom property declarations.
                    .replace(/--[a-z0-9-]+\s*:[^;]+;?/gi, '')
                    .replace(/\s*!important/g, '')
                    .trim();
                if (cleaned) el.setAttribute('style', cleaned);
                else el.removeAttribute('style');
            }
        }
    });
}

// ─────────────────────────────────────────────────────────────────────────────
// OOXML (.docx) export — real Office Open XML via the `docx` library.
// ─────────────────────────────────────────────────────────────────────────────

// Bullet numbering reference IDs used throughout the document.
const BULLET_REF = 'bullet-list';
const ORDERED_REF = 'ordered-list';

// Document-wide numbering config: two abstract lists, each with 5 indent
// levels. Bullet uses the standard disc/circle/square progression; ordered
// uses decimal. Both are registered once on the Document and referenced per
// paragraph via `numbering: { reference, level }`.
const NUMBERING_CONFIG = {
    config: [
        {
            reference: BULLET_REF,
            levels: [0, 1, 2, 3, 4].map((i) => ({
                level: i,
                format: LevelFormat.BULLET,
                text: ['•', '◦', '▪', '◦', '•'][i],
                alignment: AlignmentType.LEFT,
                style: {
                    paragraph: {
                        indent: { left: 720 * (i + 1), hanging: 260 },
                    },
                },
            })),
        },
        {
            reference: ORDERED_REF,
            levels: [0, 1, 2, 3, 4].map((i) => ({
                level: i,
                format: LevelFormat.DECIMAL,
                text: `%${i + 1}.`,
                alignment: AlignmentType.LEFT,
                style: {
                    paragraph: {
                        indent: { left: 720 * (i + 1), hanging: 260 },
                    },
                },
            })),
        },
    ],
};

const CELL_BORDERS = {
    top: { style: BorderStyle.SINGLE, size: 8, color: '999999' },
    bottom: { style: BorderStyle.SINGLE, size: 8, color: '999999' },
    left: { style: BorderStyle.SINGLE, size: 8, color: '999999' },
    right: { style: BorderStyle.SINGLE, size: 8, color: '999999' },
};

// px string → half-points (docx unit). "18px" → 27 half-pts.
function pxToHalfPoints(px: string): number {
    const n = parseInt(px, 10);
    if (!Number.isFinite(n)) return 0;
    return Math.round(n * 0.75 * 2);
}

// Tiny named-color → hex map for the CSS color keywords that show up in
// the editor (mostly from user paste). Not exhaustive — docx's shading API
// throws on anything that isn't a 6-digit hex, so we need to either resolve
// the name or return undefined to skip shading.
const NAMED_COLORS: Record<string, string> = {
    white: 'FFFFFF',
    black: '000000',
    red: 'FF0000',
    green: '008000',
    blue: '0000FF',
    yellow: 'FFFF00',
    cyan: '00FFFF',
    magenta: 'FF00FF',
    gray: '808080',
    grey: '808080',
    lightgray: 'D3D3D3',
    lightgrey: 'D3D3D3',
    darkgray: 'A9A9A9',
    darkgrey: 'A9A9A9',
    silver: 'C0C0C0',
    transparent: undefined as any, // sentinel — caller should drop the shading
};

// Normalize any CSS color value the editor might emit (#XXX, #XXXXXX,
// rgb(...), rgba(...), named color) to the 6-digit uppercase hex that the
// `docx` library's RunProperties / shading / borders expect. Returns
// undefined when the input is unparseable or transparent — caller should
// then drop the shading/color rather than crash.
function normalizeColor(color: string | undefined): string | undefined {
    if (!color) return undefined;
    const trimmed = color.trim().toLowerCase();
    if (trimmed === '' || trimmed === 'transparent' || trimmed === 'inherit' || trimmed === 'initial') return undefined;

    // #XXXXXX or #XXX
    if (trimmed.startsWith('#')) {
        const hex = trimmed.slice(1);
        if (/^[0-9a-f]{6}$/.test(hex)) return hex.toUpperCase();
        if (/^[0-9a-f]{3}$/.test(hex)) {
            return (hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2]).toUpperCase();
        }
        return undefined;
    }

    // rgb(r, g, b) / rgba(r, g, b, a) — handle integer 0-255 and percentage.
    const rgbMatch = trimmed.match(/^rgba?\(\s*([^\s,]+)[\s,]+([^\s,]+)[\s,]+([^\s,/)]+)/);
    if (rgbMatch) {
        const toByte = (raw: string): number | undefined => {
            const s = raw.trim();
            if (s.endsWith('%')) {
                const pct = parseFloat(s.slice(0, -1));
                if (!Number.isFinite(pct)) return undefined;
                return Math.max(0, Math.min(255, Math.round((pct / 100) * 255)));
            }
            const n = parseFloat(s);
            if (!Number.isFinite(n)) return undefined;
            return Math.max(0, Math.min(255, Math.round(n)));
        };
        const r = toByte(rgbMatch[1]);
        const g = toByte(rgbMatch[2]);
        const b = toByte(rgbMatch[3]);
        if (r === undefined || g === undefined || b === undefined) return undefined;
        return [r, g, b]
            .map((v) => v.toString(16).padStart(2, '0'))
            .join('')
            .toUpperCase();
    }

    // Bare 6-digit hex without #
    if (/^[0-9a-f]{6}$/.test(trimmed)) return trimmed.toUpperCase();

    // Named color
    if (Object.prototype.hasOwnProperty.call(NAMED_COLORS, trimmed)) {
        return NAMED_COLORS[trimmed] ?? undefined;
    }

    return undefined;
}

// Back-compat wrapper for sites that just want hash stripped — now routes
// through the full normalizer so rgb()/named/etc. don't crash the docx lib.
function stripHash(color: string): string {
    return normalizeColor(color) ?? '';
}

function alignmentType(align: string | undefined): (typeof AlignmentType)[keyof typeof AlignmentType] | undefined {
    switch (align) {
        case 'center':
            return AlignmentType.CENTER;
        case 'right':
            return AlignmentType.RIGHT;
        case 'justify':
            return AlignmentType.JUSTIFIED;
        case 'left':
            return AlignmentType.LEFT;
        default:
            return undefined;
    }
}

// ── Image collection + resolution ────────────────────────────────────────────

export function collectImageDocIds(value: any[]): string[] {
    const ids: string[] = [];
    const stack: any[] = [...value];
    while (stack.length) {
        const node = stack.pop();
        if (!node) continue;
        if (node.type === 'image' && node.url != null && isDocId(node.url)) {
            ids.push(String(node.url));
        }
        if (Array.isArray(node.children)) {
            for (const c of node.children) stack.push(c);
        }
    }
    return [...new Set(ids)];
}

interface ImageAsset {
    data: Uint8Array;
    width: number;
    height: number;
}

// Resolves natural image dimensions without a DOM (falls back to ratio).
function dimensionsFromBuffer(buffer: ArrayBuffer): Promise<{ width: number; height: number }> {
    return new Promise((resolve) => {
        try {
            const blob = new Blob([buffer]);
            const url = URL.createObjectURL(blob);
            const img = new Image();
            img.onload = () => {
                resolve({ width: img.naturalWidth || 480, height: img.naturalHeight || 288 });
                URL.revokeObjectURL(url);
            };
            img.onerror = () => {
                resolve({ width: 480, height: 288 });
                URL.revokeObjectURL(url);
            };
            img.src = url;
        } catch {
            resolve({ width: 480, height: 288 });
        }
    });
}

async function fetchAsBuffer(url: string): Promise<ArrayBuffer | null> {
    try {
        if (url.startsWith('data:')) {
            const b64 = url.split(',')[1];
            if (!b64) return null;
            const raw = atob(b64);
            const bytes = new Uint8Array(raw.length);
            for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i);
            return bytes.buffer;
        }
        const res = await fetch(url);
        if (!res.ok) return null;
        return await res.arrayBuffer();
    } catch {
        return null;
    }
}

export async function resolveImages(value: any[]): Promise<Map<string, ImageAsset>> {
    const map = new Map<string, ImageAsset>();

    // Collect all unique image keys (docId strings or https/data URLs).
    const keys: string[] = [];
    const stack: any[] = [...value];
    while (stack.length) {
        const node = stack.pop();
        if (!node) continue;
        if (node.type === 'image' && node.url != null) {
            const key = String(node.url);
            if (!keys.includes(key)) keys.push(key);
        }
        if (Array.isArray(node.children)) {
            for (const c of node.children) stack.push(c);
        }
    }

    if (keys.length === 0) return map;

    // Resolve docIds → HTTPS URLs first.
    const docIdKeys = keys.filter((k) => isDocId(k));
    let docIdToUrl: Record<string, string> = {};
    if (docIdKeys.length > 0) {
        try {
            docIdToUrl = await fetchImageUrlsBulk(docIdKeys);
        } catch {
            // failed bulk resolve — individual images will be omitted
        }
    }

    await Promise.all(
        keys.map(async (key) => {
            const rawUrl = String(key);

            // blob: URLs are transient — skip.
            if (rawUrl.startsWith('blob:')) return;

            const resolvedUrl = isDocId(key) ? docIdToUrl[key] ?? null : rawUrl;
            if (!resolvedUrl) return;

            const buffer = await fetchAsBuffer(resolvedUrl);
            if (!buffer) return;

            const natural = await dimensionsFromBuffer(buffer);
            const MAX_WIDTH = 600;
            const w = Math.min(natural.width || 480, MAX_WIDTH);
            const h = natural.width > 0 ? Math.round((natural.height / natural.width) * w) : Math.round(w * 0.6);

            map.set(key, { data: new Uint8Array(buffer), width: w, height: h });
        }),
    );

    return map;
}

// ── Inline node rendering ─────────────────────────────────────────────────────

export function leafToTextRun(leaf: any): TextRun {
    const sizeHp = leaf.fontSize ? pxToHalfPoints(String(leaf.fontSize)) : undefined;
    // Normalize once and check for undefined — passing an empty string or an
    // unparseable color (e.g. `rgb(255, 255, 255)` from a paste) to the docx
    // library throws "Invalid hex value". Drop the property when normalize
    // returns undefined.
    const fgColor = normalizeColor(leaf.color);
    const bgColor = normalizeColor(leaf.backgroundColor);
    return new TextRun({
        text: leaf.text ?? '',
        bold: leaf.bold ?? false,
        italics: leaf.italic ?? false,
        underline: leaf.underline ? {} : undefined,
        strike: leaf.strikethrough ?? false,
        subScript: leaf.subscript ?? false,
        superScript: leaf.superscript ?? false,
        // code styling: monospace + light grey shading
        font: leaf.code ? 'Courier New' : leaf.fontFamily ?? undefined,
        size: sizeHp && sizeHp > 0 ? sizeHp : undefined,
        color: fgColor,
        shading: bgColor ? { type: ShadingType.SOLID, color: bgColor, fill: bgColor } : undefined,
    });
}

export function inlineChildrenToRuns(children: any[], images: Map<string, ImageAsset>): (TextRun | ExternalHyperlink | ImageRun)[] {
    if (!Array.isArray(children)) return [];
    const runs: (TextRun | ExternalHyperlink | ImageRun)[] = [];

    for (const child of children) {
        if (child == null) continue;

        if (child.type === 'a') {
            const innerRuns = inlineChildrenToRuns(child.children ?? [], images).filter((r): r is TextRun => r instanceof TextRun);
            if (innerRuns.length > 0) {
                runs.push(new ExternalHyperlink({ link: child.url ?? '', children: innerRuns }));
            }
            continue;
        }

        if (child.type === 'mention') {
            runs.push(
                new TextRun({
                    text: `@${child.value ?? child.key ?? ''}`,
                    bold: true,
                    color: '6021b3',
                }),
            );
            continue;
        }

        // Inline image (rare but possible via some Plate plugins)
        if (child.type === 'image' && child.url != null) {
            const asset = images.get(String(child.url));
            if (asset) {
                runs.push(new ImageRun({ type: 'png', data: asset.data, transformation: { width: asset.width, height: asset.height } }));
            }
            continue;
        }

        // Plain text leaf
        if (typeof child.text === 'string') {
            runs.push(leafToTextRun(child));
        }
    }

    return runs;
}

// ── Block node rendering ──────────────────────────────────────────────────────

function isOrderedList(listStyleType: string | undefined): boolean {
    return ['decimal', 'lower-alpha', 'upper-alpha', 'lower-roman', 'upper-roman'].includes(listStyleType ?? '');
}

export function blockToParagraphsOrTable(node: any, images: Map<string, ImageAsset>): (Paragraph | Table)[] {
    if (!node) return [];

    const type: string = node.type ?? 'p';

    // ── table ──────────────────────────────────────────────────────────────
    if (type === 'table') {
        const sourceRows = (node.children ?? []).filter((r: any) => r?.type === 'tr');

        // Max columns across all rows — used to evenly distribute width.
        // Without explicit column widths, Google Docs shrinks every column
        // to its minimum content width (one character per line); Word lets
        // them grow. Setting an explicit per-column twip width keeps both
        // renderers honest.
        let maxCols = 0;
        for (const r of sourceRows) {
            const c = (r.children ?? []).filter((x: any) => x?.type === 'td' || x?.type === 'th').length;
            if (c > maxCols) maxCols = c;
        }

        // Usable page width = 8.5" - 2" margins = 6.5" = 9360 twips
        // (1 inch = 1440 twips). Divided evenly across columns.
        const USABLE_WIDTH_TWIPS = 9360;
        const colWidthTwips = maxCols > 0 ? Math.floor(USABLE_WIDTH_TWIPS / maxCols) : USABLE_WIDTH_TWIPS;

        const rows = sourceRows.map((row: any) => {
            const cells = (row.children ?? [])
                .filter((c: any) => c?.type === 'td' || c?.type === 'th')
                .map((cell: any) => {
                    const isHeader = cell.type === 'th';
                    const cellChildren: Paragraph[] = (cell.children ?? []).flatMap((child: any) =>
                        blockToParagraphsOrTable(child, images).filter((n): n is Paragraph => n instanceof Paragraph),
                    );
                    if (cellChildren.length === 0) {
                        cellChildren.push(new Paragraph({ children: [] }));
                    }
                    return new TableCell({
                        children: cellChildren,
                        borders: CELL_BORDERS,
                        width: { size: colWidthTwips, type: WidthType.DXA },
                        shading: isHeader ? { type: ShadingType.SOLID, color: 'f5f5f3', fill: 'f5f5f3' } : undefined,
                    });
                });
            return new TableRow({ children: cells });
        });

        if (rows.length === 0) return [];

        return [
            new Table({
                rows,
                width: { size: USABLE_WIDTH_TWIPS, type: WidthType.DXA },
                columnWidths: maxCols > 0 ? Array(maxCols).fill(colWidthTwips) : undefined,
            }),
        ];
    }

    // ── headings ──────────────────────────────────────────────────────────
    if (type === 'h1' || type === 'h2' || type === 'h3') {
        const levelMap: Record<string, (typeof HeadingLevel)[keyof typeof HeadingLevel]> = {
            h1: HeadingLevel.HEADING_1,
            h2: HeadingLevel.HEADING_2,
            h3: HeadingLevel.HEADING_3,
        };
        const runs = inlineChildrenToRuns(node.children ?? [], images);
        return [
            new Paragraph({
                children: runs,
                heading: levelMap[type],
                alignment: alignmentType(node.align ?? node.textAlign),
            }),
        ];
    }

    // ── blockquote ────────────────────────────────────────────────────────
    if (type === 'blockquote') {
        const runs = inlineChildrenToRuns(node.children ?? [], images).map((r) => {
            if (r instanceof TextRun) {
                // Re-create with italic set; TextRun is immutable so we clone.
                return new TextRun({ ...(r as any).options, italics: true });
            }
            return r;
        });
        return [
            new Paragraph({
                children: runs,
                indent: { left: 720 },
                border: {
                    left: { style: BorderStyle.SINGLE, size: 12, color: 'cccccc' },
                },
            }),
        ];
    }

    // ── hr ────────────────────────────────────────────────────────────────
    if (type === 'hr') {
        return [
            new Paragraph({
                children: [],
                border: {
                    bottom: { style: BorderStyle.SINGLE, size: 6, color: 'cccccc' },
                },
            }),
        ];
    }

    // ── image (block) ─────────────────────────────────────────────────────
    if (type === 'image') {
        const asset = node.url != null ? images.get(String(node.url)) : undefined;
        if (!asset) return [];
        return [
            new Paragraph({
                children: [new ImageRun({ type: 'png', data: asset.data, transformation: { width: asset.width, height: asset.height } })],
            }),
        ];
    }

    // ── column_group / column — flatten children ──────────────────────────
    if (type === 'column_group' || type === 'column') {
        return (node.children ?? []).flatMap((child: any) => blockToParagraphsOrTable(child, images));
    }

    // ── callout ───────────────────────────────────────────────────────────
    if (type === 'callout') {
        const runs = inlineChildrenToRuns(node.children ?? [], images);
        return [
            new Paragraph({
                children: runs,
                indent: { left: 720 },
                border: {
                    left: { style: BorderStyle.THICK, size: 12, color: '6021b3' },
                },
            }),
        ];
    }

    // ── paragraph (default, also handles list items) ──────────────────────
    const listStyle: string | undefined = node.listStyleType;
    const indentLevel = Math.max(0, (node.indent ?? 1) - 1);

    const isList = Boolean(listStyle);
    const ordered = isOrderedList(listStyle);

    const runs = inlineChildrenToRuns(node.children ?? [], images);

    return [
        new Paragraph({
            children: runs,
            alignment: alignmentType(node.align ?? node.textAlign),
            numbering: isList ? { reference: ordered ? ORDERED_REF : BULLET_REF, level: indentLevel } : undefined,
            indent: !isList && node.indent ? { left: 720 * node.indent } : undefined,
        }),
    ];
}

// ── Document body assembly ────────────────────────────────────────────────────

export function valueToBody(value: any[], images: Map<string, ImageAsset>): (Paragraph | Table)[] {
    if (!Array.isArray(value)) return [];
    return value.flatMap((node) => blockToParagraphsOrTable(node, images));
}

// ── Orchestrator ──────────────────────────────────────────────────────────────

export async function exportEditorToDocxFile({ value, filename = 'document' }: { value: any[]; filename?: string }): Promise<void> {
    const images = await resolveImages(value);
    const body = valueToBody(value, images);

    // Ensure the document always has at least one paragraph (docx rejects
    // an empty sections.children array).
    if (body.length === 0) {
        body.push(new Paragraph({ children: [] }));
    }

    const doc = new Document({
        numbering: NUMBERING_CONFIG,
        styles: {
            default: {
                document: {
                    run: { font: 'Calibri', size: 22 /* 11pt × 2 */ },
                },
                heading1: {
                    run: { font: 'Calibri', size: 40 /* 20pt */, bold: true },
                },
                heading2: {
                    run: { font: 'Calibri', size: 32 /* 16pt */, bold: true },
                },
                heading3: {
                    run: { font: 'Calibri', size: 26 /* 13pt */, bold: true },
                },
            },
        },
        sections: [
            {
                properties: {
                    page: {
                        size: {
                            // Letter: 8.5" × 11" in twentieths-of-a-point (twips). 1" = 1440 twips.
                            width: 12240,
                            height: 15840,
                        },
                        margin: {
                            top: 1440,
                            right: 1440,
                            bottom: 1440,
                            left: 1440,
                        },
                    },
                },
                children: body,
            },
        ],
    });

    const blob = await Packer.toBlob(doc);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename.endsWith('.docx') ? filename : `${filename}.docx`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
}
